import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from '@phosphor-icons/react'

export default function PrivacyPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-lg border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              Voltar
            </button>
            <img src="/logo.png" alt="Eximtech" className="h-8 w-auto" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-white mb-8">Política de Privacidade</h1>

        <div className="prose prose-invert prose-slate max-w-none">
          <p className="text-slate-300 mb-6">
            Última atualização: {new Date().toLocaleDateString('pt-BR')}
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Introdução</h2>
            <p className="text-slate-400 mb-4">
              A Eximtech está comprometida em proteger sua privacidade. Esta Política de Privacidade explica como
              coletamos, usamos, armazenamos e protegemos suas informações pessoais quando você utiliza nossa
              plataforma de gestão de fretes internacionais.
            </p>
            <p className="text-slate-400 mb-4">
              Esta política está em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)
              e demais legislações aplicáveis.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">2. Dados que Coletamos</h2>
            <h3 className="text-lg font-medium text-white mb-3">2.1. Dados fornecidos por você</h3>
            <ul className="list-disc list-inside text-slate-400 mb-4 space-y-2">
              <li>Nome completo e dados de identificação</li>
              <li>Endereço de e-mail e telefone</li>
              <li>Dados da empresa (razão social, CNPJ, endereço)</li>
              <li>Informações de login e senha (criptografada)</li>
              <li>Dados de cargas e operações de comércio exterior</li>
            </ul>
            <h3 className="text-lg font-medium text-white mb-3">2.2. Dados coletados automaticamente</h3>
            <ul className="list-disc list-inside text-slate-400 mb-4 space-y-2">
              <li>Endereço IP e dados de geolocalização aproximada</li>
              <li>Tipo de navegador e dispositivo utilizado</li>
              <li>Páginas visitadas e tempo de permanência</li>
              <li>Data e hora de acesso</li>
              <li>Cookies e tecnologias similares</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. Finalidades do Tratamento</h2>
            <p className="text-slate-400 mb-4">
              Utilizamos seus dados pessoais para as seguintes finalidades:
            </p>
            <ul className="list-disc list-inside text-slate-400 mb-4 space-y-2">
              <li>Criar e gerenciar sua conta na plataforma</li>
              <li>Processar solicitações de cotação e propostas</li>
              <li>Facilitar a comunicação entre embarcadores e agentes de carga</li>
              <li>Enviar notificações sobre suas operações</li>
              <li>Melhorar nossos serviços e experiência do usuário</li>
              <li>Cumprir obrigações legais e regulatórias</li>
              <li>Prevenir fraudes e garantir a segurança da plataforma</li>
              <li>Enviar comunicações de marketing (com seu consentimento)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. Base Legal para o Tratamento</h2>
            <p className="text-slate-400 mb-4">
              Tratamos seus dados pessoais com base nas seguintes hipóteses legais previstas na LGPD:
            </p>
            <ul className="list-disc list-inside text-slate-400 mb-4 space-y-2">
              <li><strong className="text-white">Execução de contrato:</strong> Para prestar os serviços contratados</li>
              <li><strong className="text-white">Consentimento:</strong> Para envio de comunicações de marketing</li>
              <li><strong className="text-white">Legítimo interesse:</strong> Para melhorar nossos serviços e prevenir fraudes</li>
              <li><strong className="text-white">Obrigação legal:</strong> Para cumprir exigências legais e regulatórias</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. Compartilhamento de Dados</h2>
            <p className="text-slate-400 mb-4">
              Podemos compartilhar seus dados pessoais com:
            </p>
            <ul className="list-disc list-inside text-slate-400 mb-4 space-y-2">
              <li>Outros usuários da plataforma (embarcadores e agentes de carga) conforme necessário para as operações</li>
              <li>Prestadores de serviços que nos auxiliam na operação da plataforma</li>
              <li>Autoridades governamentais quando exigido por lei</li>
              <li>Parceiros de negócios, mediante seu consentimento</li>
            </ul>
            <p className="text-slate-400 mb-4">
              Não vendemos seus dados pessoais a terceiros.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Segurança dos Dados</h2>
            <p className="text-slate-400 mb-4">
              Implementamos medidas técnicas e organizacionais para proteger seus dados, incluindo:
            </p>
            <ul className="list-disc list-inside text-slate-400 mb-4 space-y-2">
              <li>Criptografia de dados em trânsito e em repouso</li>
              <li>Controles de acesso baseados em função</li>
              <li>Monitoramento contínuo de segurança</li>
              <li>Backups regulares</li>
              <li>Treinamento de funcionários em segurança da informação</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">7. Retenção de Dados</h2>
            <p className="text-slate-400 mb-4">
              Mantemos seus dados pessoais pelo tempo necessário para cumprir as finalidades para as quais foram
              coletados, incluindo obrigações legais, contratuais e regulatórias. Dados de operações comerciais
              são mantidos pelo prazo legal mínimo de 5 anos.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">8. Seus Direitos</h2>
            <p className="text-slate-400 mb-4">
              De acordo com a LGPD, você tem os seguintes direitos:
            </p>
            <ul className="list-disc list-inside text-slate-400 mb-4 space-y-2">
              <li><strong className="text-white">Confirmação e acesso:</strong> Saber se tratamos seus dados e acessá-los</li>
              <li><strong className="text-white">Correção:</strong> Solicitar a correção de dados incompletos ou desatualizados</li>
              <li><strong className="text-white">Anonimização ou eliminação:</strong> Solicitar a anonimização ou exclusão de dados desnecessários</li>
              <li><strong className="text-white">Portabilidade:</strong> Solicitar a transferência de seus dados a outro fornecedor</li>
              <li><strong className="text-white">Revogação do consentimento:</strong> Retirar seu consentimento a qualquer momento</li>
              <li><strong className="text-white">Oposição:</strong> Opor-se ao tratamento em determinadas circunstâncias</li>
            </ul>
            <p className="text-slate-400 mb-4">
              Para exercer seus direitos, entre em contato através do e-mail: privacidade@eximtech.com.br
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">9. Cookies</h2>
            <p className="text-slate-400 mb-4">
              Utilizamos cookies e tecnologias similares para melhorar sua experiência na plataforma. Os tipos de
              cookies que utilizamos incluem:
            </p>
            <ul className="list-disc list-inside text-slate-400 mb-4 space-y-2">
              <li><strong className="text-white">Cookies essenciais:</strong> Necessários para o funcionamento da plataforma</li>
              <li><strong className="text-white">Cookies de desempenho:</strong> Para analisar como você usa a plataforma</li>
              <li><strong className="text-white">Cookies de funcionalidade:</strong> Para lembrar suas preferências</li>
            </ul>
            <p className="text-slate-400 mb-4">
              Você pode configurar seu navegador para recusar cookies, mas isso pode afetar o funcionamento da plataforma.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">10. Transferência Internacional</h2>
            <p className="text-slate-400 mb-4">
              Seus dados podem ser transferidos e armazenados em servidores localizados fora do Brasil. Nestes casos,
              garantimos que sejam aplicadas medidas de proteção adequadas conforme exigido pela LGPD.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">11. Alterações nesta Política</h2>
            <p className="text-slate-400 mb-4">
              Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre mudanças
              significativas através da plataforma ou por e-mail. Recomendamos que você revise esta política
              regularmente.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">12. Contato</h2>
            <p className="text-slate-400 mb-4">
              Para dúvidas sobre esta Política de Privacidade ou para exercer seus direitos, entre em contato:
            </p>
            <p className="text-slate-400 mb-2">
              <strong className="text-white">Encarregado de Proteção de Dados (DPO):</strong>
            </p>
            <p className="text-slate-400">
              E-mail: privacidade@eximtech.com.br
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-900 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Eximtech. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  )
}
